/* ──────────────────────────────────────────────────────────────────────────
   Chart foundation tokens — "Radiant" / gains-grade glow in the ARCrypto palette.
   Pure module (no React). Colours are NOT hardcoded here: they name the v4 CSS
   variables (index.css :root / [data-theme]) and are resolved at runtime by
   useChartTheme, so every chart re-themes (and dials its glow back) with the app.
   Spec: docs/CHART_STYLE.md §2–§5.
   ────────────────────────────────────────────────────────────────────────── */

/* Glow intensity dial (§2 + §5). Dark = full gains-grade bloom; light dialed back
   so heavy blur doesn't read muddy on the ivory theme. */
export const GLOW_STD = { dark: 5, light: 2 };

/* Directional series → v4 token names (§1, §3). Gold is primary; green/red signed.
   Per-tab signature tones follow: each names a CSS var (index.css) so it re-themes
   and dials back in light like the primary tones. Amber reuses --arc-warn. */
export const TONE_VARS = {
  gold: '--arc-accent', // primary series
  pos:  '--arc-pos',    // positive / income
  neg:  '--arc-neg',    // negative / expense
  coral:   '--chart-coral',
  amber:   '--arc-warn',
  blue:    '--chart-blue',
  cyan:    '--chart-cyan',
  magenta: '--chart-magenta',
};

/* NEON categorical set for the donut — the ONE place non-gold is sanctioned (§3).
   Gold/green/red come from tokens so they re-theme; violet & teal have no token in
   the v4 set, so they're defined here as the spec's pie-only exception. */
export const NEON_VIOLET = '#A78BFA';
export const NEON_TEAL = '#4FD1C5';
export const NEON_VARS = ['--arc-accent', '--arc-pos', NEON_VIOLET, NEON_TEAL, '--arc-neg'];

/* Per-instance id factory. SVG <defs> ids are document-global, so several charts on
   one page would collide on url(#id) refs — pair this with React's useId() to give
   every chart instance its own namespaced filter/gradient ids. */
export const makeChartIds = (uid) => {
  const u = String(uid).replace(/[^a-zA-Z0-9_-]/g, '');
  const toneIds = (prefix) =>
    Object.fromEntries(Object.keys(TONE_VARS).map((t) => [t, `${prefix}-${t}-${u}`]));
  return {
    glow: `arcGlow-${u}`,
    line: toneIds('arcLine'),
    area: toneIds('arcArea'),
    bar:  `arcBar-${u}`,
  };
};

/* Blend a resolved hex toward a target hex by `amt` (0–1). Used to derive the bright
   top stop of a line gradient FROM its token — so the lighter shade still tracks the
   theme. Leaves non-hex values (e.g. rgba tokens) untouched. */
const parseHex = (h) => [1, 3, 5].map((i) => parseInt(h.slice(i, i + 2), 16));
const toHex = (rgb) =>
  '#' + rgb.map((n) => Math.max(0, Math.min(255, Math.round(n))).toString(16).padStart(2, '0')).join('');

export const mix = (hex, target, amt) => {
  if (!hex || hex[0] !== '#' || hex.length < 7) return hex;
  const a = parseHex(hex);
  const b = parseHex(target);
  return toHex(a.map((v, i) => v + (b[i] - v) * amt));
};

export const lighten = (hex, amt = 0.45) => mix(hex, '#FFFFFF', amt);
